import { useState, useEffect } from "react";
import { Search, Loader2, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FollowingListItem } from "./FollowingListItem";
import { useFollowingStores } from "@/hooks/useFollowingStores";
import { extractErrorMessage } from "@/utils/errorMessageHandler";
import { authToasts, followToasts } from "@/utils/toastHelpers";
import { useNavigate } from "react-router-dom";

interface FollowingDialogProps {
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function FollowingDialog({
  trigger,
  open,
  onOpenChange,
}: FollowingDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [unfollowingStoreId, setUnfollowingStoreId] = useState<string | null>(
    null
  );
  const ITEMS_PER_PAGE = 20;

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const {
    stores,
    isLoading,
    pagination,
    unfollowStore,
    isUnfollowing,
    unfollowError,
    error: fetchError,
  } = useFollowingStores({
    page: currentPage,
    limit: ITEMS_PER_PAGE,
    query: debouncedQuery,
    enabled: open, // Only fetch when dialog is open
  });

  
  useEffect(() => {
    if (!open) {
      setCurrentPage(1);
      setSearchQuery("");
      setDebouncedQuery("");
      setUnfollowingStoreId(null);
    }
  }, [open]);

  const navigate = useNavigate();

  const handleStoreClick = (storeId: string) => {
    navigate(`/profile/${storeId}`);
  };

  const handleUnfollow = async (sellerId: string) => {
    try {
      setUnfollowingStoreId(sellerId);

      
      const store = stores.find((s) => s.id === sellerId);

      await unfollowStore(sellerId);
      setUnfollowingStoreId(null);


      followToasts.unfollowSuccess(store?.name);
    } catch (error) {
      const errorMessage = extractErrorMessage(error as Error);
      setUnfollowingStoreId(null);

      // Error toast
      followToasts.unfollowError(errorMessage);
    }
  };

  const handleNextPage = () => {
    if (pagination?.hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Check if error is authentication related
  const isAuthError =
    fetchError &&
    (extractErrorMessage(fetchError).toLowerCase().includes("unauthorized") ||
      extractErrorMessage(fetchError)
        .toLowerCase()
        .includes("unauthenticated") ||
      extractErrorMessage(fetchError).toLowerCase().includes("forbidden"));

  // Show session expired toast for auth errors
  useEffect(() => {
    if (isAuthError && open) {
      authToasts.sessionExpired();
    }
  }, [isAuthError, open]);



  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-[37.5rem] w-[calc(100vw-2rem)] sm:w-full p-0 gap-0 rounded-[5px] shadow-[0px_4px_4px_rgba(0,0,0,0.25)] border-[rgba(0,0,0,0.05)] max-h-[90vh] flex flex-col">
        <DialogHeader className="p-4 sm:p-6 pb-3 flex-shrink-0">
          <DialogTitle className="text-center font-lexend text-[18px] sm:text-[20px] font-medium leading-normal tracking-[-0.22px] capitalize text-[rgba(0,0,0,0.80)]">
            Following
          </DialogTitle>
        </DialogHeader>

        {/* Error Messages */}
        {(fetchError || unfollowError) && (
          <div className="px-4 sm:px-6 pb-3 flex-shrink-0">
            {isAuthError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="font-lexend text-[13px]">
                  Please log in to view your following list.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="font-lexend text-[13px]">
                  {unfollowError || extractErrorMessage(fetchError as Error)}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <div className="px-4 sm:px-6 pb-3 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-[17px] text-[rgba(0,0,0,0.50)]" />
            <Input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={isAuthError}
              className="pl-10 h-[43px] bg-[rgba(0,0,0,0.05)] border-[rgba(0,0,0,0.05)] rounded-[5px] font-lexend text-[15px] font-light tracking-[-0.22px] capitalize placeholder:text-[rgba(0,0,0,0.30)] disabled:opacity-50"
            />
          </div>
        </div>

        <div className="max-h-[400px] sm:max-h-[400px] overflow-y-auto flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="size-8 animate-spin text-gray-400" />
            </div>
          ) : isAuthError ? (
            <div className="flex flex-col items-center justify-center py-12 px-6">
              <AlertCircle className="size-12 text-gray-400 mb-3" />
              <p className="text-[15px] text-gray-500 font-lexend text-center">
                Authentication required
              </p>
              <p className="text-[13px] text-gray-400 font-lexend text-center mt-1">
                Please log in to view and manage your following list
              </p>
            </div>
          ) : stores.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-[15px] text-gray-500 font-lexend">
                {debouncedQuery
                  ? "No stores found"
                  : "Not following any stores yet"}
              </p>
            </div>
          ) : (
            stores.map((store, index) => (
              <FollowingListItem
                key={store.id}
                store={store}
                isLast={index === stores.length - 1}
                onStoreClick={handleStoreClick}
                onUnfollow={handleUnfollow}
                isUnfollowing={isUnfollowing && unfollowingStoreId === store.id}
              />
            ))
          )}
        </div>

        {/* Pagination Controls */}
        {pagination && pagination.pages > 1 && !debouncedQuery && !isAuthError && (
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-t border-[rgba(0,0,0,0.05)] flex-shrink-0">
            <Button
              variant="ghost"
              onClick={handlePrevPage}
              disabled={currentPage === 1 || isLoading}
              className="font-lexend text-[14px] font-normal tracking-[-0.22px] capitalize text-black hover:bg-[rgba(0,0,0,0.05)] disabled:opacity-50"
            >
              Previous
            </Button>

            <span className="font-lexend text-[14px] font-normal text-[rgba(0,0,0,0.60)]">
              Page {pagination.current} of {pagination.pages}
            </span>

            <Button
              variant="ghost"
              onClick={handleNextPage}
              disabled={!pagination.hasMore || isLoading}
              className="font-lexend text-[14px] font-normal tracking-[-0.22px] capitalize text-black hover:bg-[rgba(0,0,0,0.05)] disabled:opacity-50"
            >
              Next
            </Button>
          </div>
        )}

        {/* Total count */}
        {pagination && pagination.total > 0 && !isAuthError && (
          <div className="px-4 sm:px-6 pb-4 text-center flex-shrink-0">
            <p className="font-lexend text-[12px] text-[rgba(0,0,0,0.50)]">
              {debouncedQuery
                ? `Showing ${stores.length} of ${pagination.total} stores`
                : `Following ${pagination.total} ${pagination.total === 1 ? "store" : "stores"}`}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
